import type { ReactNode } from "react";
import React, { createContext, useCallback, useContext, useMemo } from "react";

import { useMutation, useQuery } from "@tanstack/react-query";

import ApiHttp from "../ApiHttp";
import { useLibrary } from "../dbbl/providers/useLibrary.hook";
import OrganizationUser from "../OrganizationUser";
import User from "../User";
import { Role } from "../Role";

type OrganizationUserContextType = {
  isLoading: boolean;
  organizationUsers: OrganizationUser[];
  error?: Error;
  refresh: () => void;
  createOrganizationUser: (
    payload: API.CreateOrganizationUser.Request,
  ) => Promise<OrganizationUser>;
  updateOrganizationUser: (params: {
    uuid: string;
    payload: API.UpdateOrganizationUser.Request;
  }) => Promise<OrganizationUser>;
  deactivateOrganizationUser: (uuid: string) => Promise<void>;
};

const organizationUserContext = createContext<OrganizationUserContextType>({
  isLoading: false,
  organizationUsers: [],
  error: undefined,
  refresh: () => {},
  createOrganizationUser: () => {
    const user = new User({
      first_name: "",
      last_name: "",
      email: "",
      phone: { number: "" },
    });
    const role = new Role({ name: "" });
    return Promise.resolve(
      new OrganizationUser({
        uuid: "",
        user,
        role,
        status: "",
        organization_uuid: "",
      }),
    );
  },
  updateOrganizationUser: () => {
    const user = new User({
      first_name: "",
      last_name: "",
      email: "",
      phone: { number: "" },
    });
    const role = new Role({ name: "", uuid: "required" });
    return Promise.resolve(
      new OrganizationUser({
        uuid: "required",
        user,
        role,
        status: "",
        organization_uuid: "",
      }),
    );
  },
  deactivateOrganizationUser: () => Promise.resolve(),
});

export const useOrganizationUserContext = () =>
  useContext(organizationUserContext);

export const OrganizationUserProvider = ({
  orgUuid,
  children,
}: {
  orgUuid: UUID | Brand<string, "OrganizationId">;
  children: ReactNode;
}) => {
  const [lastUpdateRequested, setLastUpdateRequested] = React.useState<Date>(
    new Date(),
  );
  const { token, apiUrl } = useLibrary("network");
  const { isLoading, data, error } = useQuery({
    queryKey: [
      "organization_users",
      orgUuid,
      lastUpdateRequested.getTime(),
      token,
      apiUrl,
    ],
    queryFn: () =>
      ApiHttp.fetch<{ organization_users: unknown[] }>(
        `${apiUrl}/organizations/${orgUuid}/users`,
        {
          token,
          endpointIsUrl: true,
        },
      ).then((responseData) =>
        responseData.organization_users
          .map((u) => OrganizationUser.deserialize(u as never))
          .filter((u): u is OrganizationUser => u !== null),
      ),
    enabled: !!orgUuid && !!token,
  });
  const refresh = useCallback(() => setLastUpdateRequested(new Date()), []);
  const createMutation = useMutation({
    mutationFn: (payload: API.CreateOrganizationUser.Request) =>
      ApiHttp.fetch<{ organization_user: unknown }>(
        `${apiUrl}/organizations/${orgUuid}/users`,
        { method: "POST", token, endpointIsUrl: true },
        payload,
      ).then((responseData) => {
        const orgUser = OrganizationUser.deserialize(
          responseData.organization_user as never,
        );
        if (!orgUser) throw new Error("Failed to create organization user");
        return orgUser;
      }),
    onSuccess: refresh,
  });
  const updateMutation = useMutation({
    mutationFn: ({
      uuid,
      payload,
    }: {
      uuid: string;
      payload: API.UpdateOrganizationUser.Request;
    }) =>
      ApiHttp.fetch<{ organization_user: unknown }>(
        `${apiUrl}/organizations/${orgUuid}/user/${uuid}`,
        { method: "PUT", token, endpointIsUrl: true },
        payload,
      ).then((responseData) => {
        const orgUser = OrganizationUser.deserialize(
          responseData.organization_user as never,
        );
        if (!orgUser) throw new Error("Failed to update organization user");
        return orgUser;
      }),
  });
  const deactivateMutation = useMutation({
    mutationFn: (uuid: string) =>
      ApiHttp.fetch<void>(`${apiUrl}/organizations/${orgUuid}/user/${uuid}`, {
        method: "DELETE",
        token,
        endpointIsUrl: true,
      }),
    onSuccess: refresh,
  });
  const value = useMemo(() => {
    return {
      isLoading,
      organizationUsers: data || [],
      error: error || undefined,
      refresh,
      createOrganizationUser: createMutation.mutateAsync,
      updateOrganizationUser: updateMutation.mutateAsync,
      deactivateOrganizationUser: deactivateMutation.mutateAsync,
    };
  }, [
    isLoading,
    data,
    error,
    refresh,
    createMutation.mutateAsync,
    updateMutation.mutateAsync,
    deactivateMutation.mutateAsync,
  ]);
  return (
    <organizationUserContext.Provider value={value}>
      {children}
    </organizationUserContext.Provider>
  );
};
